import { useCallback, useState } from 'react';

type RecordOrEntries<V> = Record<string, V> | [string, V][];

// Public interface
interface Actions<V> {
  set: (key: string, value: V) => void;
  update: (key: string, value: Partial<V>) => void;
  upsert: (key: string, value: V) => void;
  setAll: (entries: RecordOrEntries<V>) => void;
  remove: (key: string) => void;
  reset: () => void;
  map: Array<[string, V]>['map'];
  toArray: () => Array<[string, V]>;
  length: () => number;
}

type Return<V> = [Record<string, V>, Actions<V>];

export function useObjectMap<V>(
  initialState: RecordOrEntries<V> = {}
): Return<V> {
  const [map, setMap] = useState<Record<string, V>>(
    Array.isArray(initialState)
      ? Object.fromEntries(initialState)
      : { ...initialState }
  );

  const actions: Actions<V> = {
    set: useCallback((key, value) => {
      setMap((prev) => ({ ...prev, [key]: value }));
    }, []),
    update: useCallback((key, value) => {
      setMap((prev) =>
        prev[key] !== undefined
          ? { ...prev, [key]: { ...prev[key], ...value } }
          : prev
      );
    }, []),
    upsert: useCallback((key, value) => {
      setMap((prev) => ({ ...prev, [key]: { ...prev[key], ...value } }));
    }, []),

    setAll: useCallback((entries) => {
      setMap(
        Array.isArray(entries) ? Object.fromEntries(entries) : { ...entries }
      );
    }, []),

    remove: useCallback((key) => {
      setMap((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }, []),

    reset: useCallback(() => {
      setMap({});
    }, []),
    map: useCallback((...args) => Object.entries(map)?.map(...args), [map]),
    toArray: useCallback(() => Object.entries(map), [map]),
    length: useCallback(() => Object.keys(map).length, [map]),
  };

  return [map, actions];
}
